/**
 * Shift history (POS Phase 2) — the Z-report archive a manager reviews: every
 * register close with its expected vs counted cash, variance, and the cashier's
 * remark. Gated on `pos.shift.manage`. Built on the shared master-list chrome
 * (DataTableCard + ListPagination), matching Customers / Payroll.
 * @see prototype/pos/shift-close.html (S42)
 */
import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Alert } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { paiseToInr, paiseToInrShort } from "../../../../lib/format.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { DataTableCard, ListPagination } from "../../../../features/master-list/index.ts";
import { CloseRegisterPage } from "../../../../features/pos/close-register-page.tsx";

export const Route = createFileRoute("/(workspace)/pos/shifts/")({
  component: ShiftHistoryPage,
});

const PAGE_SIZE = 50;

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

function ShiftHistoryPage() {
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];
  const [closingShiftId, setClosingShiftId] = useState<string | null>(null);

  const list = trpc.pos.listShifts.useQuery(
    { limit: PAGE_SIZE, ...(cursor !== undefined ? { cursor } : {}) },
    { placeholderData: (prev) => prev },
  );

  // A manager can close an OPEN shift from here (e.g. a cashier left the till
  // open) — it runs the same Z-report count flow, just for that shift id.
  if (closingShiftId) {
    return (
      <CloseRegisterPage
        shiftId={closingShiftId}
        onClosed={() => {
          setClosingShiftId(null);
          void list.refetch();
        }}
        onCancel={() => setClosingShiftId(null)}
      />
    );
  }

  const rows = list.data?.items ?? [];
  const nextCursor = list.data?.nextCursor;

  const pageNumber = cursorStack.length + 1;
  const showingFrom = rows.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + rows.length;
  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => {
    if (nextCursor) setCursorStack((s) => [...s, nextCursor]);
  };

  const shortCount = rows.filter((r) => r.variancePaise !== null && BigInt(r.variancePaise) < 0n).length;
  const netVariance = rows.reduce((a, r) => a + (r.variancePaise !== null ? BigInt(r.variancePaise) : 0n), 0n);

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Point of Sale" }, { label: "Shift history" }]}>
      <div className="space-y-4">
        {/* Head */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-ink">Shift history</h1>
            <p className="mt-0.5 text-sm text-muted">
              Every register close — the Z-report archive. Variance = counted − expected.
            </p>
          </div>
          <Link
            to="/pos"
            className="inline-flex h-10 items-center rounded-sm bg-ink px-4 text-sm font-semibold text-white hover:bg-ink-2"
          >
            Open register →
          </Link>
        </div>

        {/* Table */}
        {list.error ? (
          <Alert intent="bad" title="Couldn't load shifts" description={list.error.message} />
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-line bg-paper p-10 text-center">
            <p className="text-sm font-semibold text-ink">No shifts yet</p>
            <p className="mt-1 text-sm text-muted">
              Open and close a register to see the Z-report archive here.
            </p>
            <Link
              to="/pos"
              className="mt-4 inline-flex h-10 items-center rounded-sm bg-ink px-4 text-sm font-semibold text-white hover:bg-ink-2"
            >
              Open register →
            </Link>
          </div>
        ) : (
          <DataTableCard
            footer={[
              { label: "Visible", value: rows.length },
              { label: "Short", value: shortCount },
              {
                label: "Net variance",
                value: `${netVariance < 0n ? "−" : ""}${paiseToInrShort((netVariance < 0n ? -netVariance : netVariance).toString())}`,
              },
            ]}
            pagination={
              <ListPagination
                pageNumber={pageNumber}
                showingFrom={showingFrom}
                showingTo={showingTo}
                sortLabel="sorted by opened (newest first)"
                hasPrev={cursorStack.length > 0}
                hasNext={!!nextCursor}
                onPrev={handlePrev}
                onNext={handleNext}
              />
            }
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-raised text-left text-xs uppercase tracking-wider text-muted">
                  <th className="px-4 py-3 font-semibold">Opened</th>
                  <th className="px-4 py-3 font-semibold">Register</th>
                  <th className="px-4 py-3 font-semibold">Cashier</th>
                  <th className="px-4 py-3 text-right font-semibold">Float</th>
                  <th className="px-4 py-3 text-right font-semibold">Expected</th>
                  <th className="px-4 py-3 text-right font-semibold">Counted</th>
                  <th className="px-4 py-3 text-right font-semibold">Variance</th>
                  <th className="px-4 py-3 font-semibold">Reason</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const v = s.variancePaise === null ? null : BigInt(s.variancePaise);
                  const vClass = v === null ? "text-muted" : v < 0n ? "text-bad" : v > 0n ? "text-warn" : "text-ok";
                  const pill =
                    s.status === "open"
                      ? { label: "Open", className: "bg-sky-50 text-sky-700" }
                      : { label: "Closed", className: "bg-bg text-ink-2" };
                  return (
                    <tr key={s.id} className="border-b border-line last:border-0 hover:bg-bg">
                      <td className="px-4 py-3 font-semibold text-ink">{fmtDateTime(s.openedAt)}</td>
                      <td className="px-4 py-3 text-ink-2">{s.registerName}</td>
                      <td className="px-4 py-3 text-ink-2">{s.cashierName}</td>
                      <td className="px-4 py-3 text-right font-mono text-muted">{paiseToInr(s.openingFloatPaise)}</td>
                      <td className="px-4 py-3 text-right font-mono text-ink">
                        {s.expectedCashPaise ? paiseToInr(s.expectedCashPaise) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-ink">
                        {s.countedCashPaise ? paiseToInr(s.countedCashPaise) : "—"}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono font-semibold ${vClass}`}>
                        {v === null
                          ? "—"
                          : v === 0n
                            ? paiseToInr("0")
                            : `${v < 0n ? "−" : "+"}${paiseToInr((v < 0n ? -v : v).toString())}`}
                      </td>
                      <td className="max-w-[240px] truncate px-4 py-3 text-muted" title={s.varianceReason ?? ""}>
                        {s.varianceReason ?? "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${pill.className}`}>
                            {pill.label}
                          </span>
                          {s.status === "open" && (
                            <button
                              type="button"
                              onClick={() => setClosingShiftId(s.id)}
                              className="inline-flex h-7 items-center rounded-sm border border-line bg-paper px-2.5 text-xs font-semibold text-ink hover:bg-bg"
                              title="Close this register (run the Z-report)"
                            >
                              Close
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </DataTableCard>
        )}
      </div>
    </WorkspaceShell>
  );
}
